import CacheUtil from "../../../../../shared/lib/html5-software-library/src/ranj/util/CacheUtil";
import { getURLParameter } from "../../../../../shared/lib/html5-software-library/src/ranj/util/URLUtil";
import GameConfig from "../model/GameConfig";

export default class RoadBuilder {

    /**
     * @param {GameConfig} gameConfig 
     * @param {Object} roadAssets 
     */
    constructor(gameConfig, roadAssets) {
        this._config = gameConfig;
        this._roadAssets = roadAssets;

        this._initialized = false;
    }

    initialize() {
        if (!this._initialized) {
            this._initialized = true;
            this._laneWidth = this._config.getValue("laneWidth");
            this._sectionLength = this._config.getValue("sectionLength");
            this._tileHeight = this._config.getValue("tileHeight");
            this._shapesDeltaY = this._config.getValue("shapesSpacing");
            this._shapesScale = this._config.getValue("shapesScale");
            this._roadTypes = this._config.getValue("roadTypes");
            if (!isNaN(parseInt(getURLParameter("dy")))) {
                this._shapesDeltaY = parseInt(getURLParameter("dy"));
            }
            //Make sure shapes tile nicely:
            this._shapesDeltaY = this._sectionLength / Math.round(this._sectionLength / this._shapesDeltaY);

            if (!isNaN(parseFloat(getURLParameter("scale")))) {
                this._shapesScale = parseFloat(getURLParameter("scale"));
            }

            this.generateLaneTiles();
        }
    }

    generateLaneTiles() {
        //create images for all combinations of colors and shapes and store them for later use
        const combis = {};
        this._config.sections.forEach(sectionCfg => {
            sectionCfg.lanes.forEach(laneCfg => {
                const { color, shape } = laneCfg;
                combis[getLaneTileKey(color, shape)] = { color, shape };
            });
        });
        this._laneTileImages = {};
        const w = this._laneWidth, h = this._tileHeight + 1;
        Object.values(combis).forEach(({ color, shape }) => {
            const skin = this._buildLaneSkin(color, shape, this._tileHeight);
            CacheUtil.cache(skin, -w / 2, -h, w, h);
            const dataUrl = skin.cacheCanvas.toDataURL();
            const image = new Image(w, h);
            image.src = dataUrl;
            this._laneTileImages[getLaneTileKey(color, shape)] = image;
            //Setting canvas dimensions to 0 to make iOS remove the data from memory
            //  See https://stackoverflow.com/a/64557130/867588
            skin.cacheCanvas.width = 0;
            skin.cacheCanvas.height = 0;
            CacheUtil.uncache(skin);
        });
    }

    getLaneTileBitmap(color, shape) {
        const img = this._laneTileImages[getLaneTileKey(color, shape)];
        const bmp = new createjs.Bitmap(img);
        bmp.x = -this._laneWidth / 2;
        bmp.y = -this._tileHeight;
        return bmp;
    }

    build(sectionsContainer, markersContainer, cacheEnabled = false) {
        this.initialize();
        const sectionCfgs = this._config.sections;
        const dx = this._laneWidth;
        const dy = -this._sectionLength;
        let y = 0;
        const n = this._config.laneCount;
        const sectionSkins = sectionCfgs.map((sectionCfg, idx) => {
            const skin = sectionsContainer.addChild(new createjs.Container());
            const x0 = -dx * .5 * (n - 1);
            sectionCfg.lanes.forEach((laneCfg, laneIdx) => {
                const laneSkin = this._buildLaneSkin(laneCfg.color, laneCfg.shape, this._sectionLength);
                skin.addChild(laneSkin);
                laneSkin.x = x0 + laneIdx * dx;
                
            });
            if (sectionCfg.marker) {
                const { skin: markerSkinId, position } = sectionCfg.marker;
                const markerSkin = markersContainer.addChild(new this._roadAssets[markerSkinId]());
                markerSkin.y = y - position - (position < 0 ? this._sectionLength : 0);
            }
            if (sectionCfg.roadDividers) {
                const tileAmountOnY = Math.ceil(this._sectionLength / this._tileHeight);
                for (let xi = 1; xi < 4; xi++) {
                    for (let yi = 0; yi < tileAmountOnY; yi++) {
                        const roadDividerSkin = markersContainer.addChild(new this._roadAssets[RoadBuilder.ROAD_DIVIDER_SKIN]());
                        roadDividerSkin.snapToPixel = false;
                        roadDividerSkin.x = x0 + xi * dx - this._laneWidth * .5;
                        roadDividerSkin.y = y - this._sectionLength - this._tileHeight * yi;
                    }
                }
            }
            skin.addChild(new this._roadAssets.SectionDividerSkin());
            skin.y = y;
            y += dy;
            skin.index = idx++;
            if (cacheEnabled) {
                CacheUtil.cache(skin);
                skin.removeAllChildren();
            }
            return skin;
        });

        return sectionSkins;
    }

    _buildLaneSkin(color, shape, length) {
        const shapeId = this._config.getLaneTileSkin(shape);
        const colorTileId = this._config.getLaneTileSkin(color);

        const nTiles = Math.ceil(length / this._tileHeight);

        const skin = new createjs.Container();
        const key = this._laneTileImages[getLaneTileKey(color, shape)];
        if (this._laneTileImages[key] != null) {
            const w = this._laneWidth, h = this._tileHeight + 1;
            for (let i = 0; i < nTiles; i++) {
                const bmp = this.getLaneTileBitmap(color, shape);
                skin.addChild(bmp);
                bmp.y = -h + i * dy;
                bmp.x = -w / 2;
            }
        } else {

            //Color tiles:
            const colorTileClass = this._roadAssets[colorTileId];
            const dy = -this._tileHeight;
            for (let i = 0; i < nTiles; i++) {
                skin.addChild(new colorTileClass()).y = i * dy;
            }
            //Shapes:
            const shapeClass = shapeId ? this._roadAssets[shapeId] : null;
            if (shapeClass) {
                const nShapes = Math.round(length / this._shapesDeltaY);
                const y0 = -this._shapesDeltaY / 2;
                for (let i = 0; i < nShapes; i++) {
                    const shape = skin.addChild(new shapeClass());
                    shape.y = y0 + i * -this._shapesDeltaY;
                    shape.scaleX = shape.scaleY = this._shapesScale;
                }
            }
        }
        return skin;
    }
}

const getLaneTileKey = function(color, shape) {
    return `${color}${shape}`;
}

RoadBuilder.ROAD_DIVIDER_SKIN = "RoadTileSeparator";